import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import Layout from './components/Layout';
import MenuPage from './pages/MenuPage';
import PlayScreen from './pages/PlayScreen';
import LeaderboardPage from './pages/LeaderboardPage';
import ProfilePage from './pages/ProfilePage';
import SubscriptionPage from './pages/SubscriptionPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import LoginPage from './pages/LoginPage';
import { useAppStore } from './store/appStore';
import useNativeSetup from './hooks/useNativeSetup';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 120000, retry: 1, refetchOnWindowFocus: false } },
});

function ThemeSync() {
  const nightMode = useAppStore((s) => s.nightMode);
  const setNightMode = useAppStore((s) => s.setNightMode);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', nightMode);
  }, [nightMode]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setNightMode(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [setNightMode]);

  return null;
}

export default function App() {
  useNativeSetup();
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeSync />
      <BrowserRouter>
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<MenuPage />} />
            <Route path="leaderboard" element={<LeaderboardPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="subscribe" element={<SubscriptionPage />} />
            <Route path="payment-success" element={<PaymentSuccessPage />} />
          </Route>
          <Route path="play/:mode" element={<PlayScreen />} />
          <Route path="login" element={<LoginPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster
        position="top-center"
        containerStyle={{ top: 'env(safe-area-inset-top, 0px)' }}
        toastOptions={{ duration: 3000, style: { borderRadius: '8px', fontSize: '14px' } }}
      />
    </QueryClientProvider>
  );
}
